import React, { useCallback, forwardRef } from 'react';
import CustomerChannelsIcon from '@atlaskit/icon/core/migration/screen';
import PopupDI, { type TriggerProps } from '@atlaskit/popup';
import { Box, xcss } from '@atlaskit/primitives';
import { useChannelsDialog as useChannelsDialogDI } from '@atlassian/jira-channels-jsd-services/src/controllers/channels-dialog/index.tsx';
import { MenuItem as MenuItemDI } from '@atlassian/jira-navigation-apps-sidebar-common/src/common/ui/menu-item/main.tsx';
import { CustomerChannelsDialog } from './customer-channels-dialog/index.tsx';
import type { CustomerChannelsItemProps } from './types.tsx';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const PopupComponent = forwardRef<HTMLDivElement, any>((props, ref) => (
	// PLEASE NOTE: The reason for the suppression of the react/jsx-props-no-spreading rule is that
	//              this is exactly following the example from the official documentation, see
	//              https://atlassian.design/components/popup/examples#customization
	//
	//              Spreading props doesn't appear to break behaviour, and it's been confirmed with
	//              ADS that this is the correct way to do it, see https://atlassian.slack.com/archives/CFJ9DU39U/p1708055215799369
	// eslint-disable-next-line react/jsx-props-no-spreading
	<Box {...props} xcss={ChannelDialogWrapperStyles} ref={ref} />
));

const POPUP_OFFSET: [number, number] = [0, 0];

/**
 * @deprecated This is a Nav3 component. If you do any changes here, please apply and QA them at Nav4 component as well: src/packages/navigation-apps/sidebar-nav4/jsm-project-menu
 */
export const CustomerChannelsItem = ({
	AkPopup = PopupDI,
	MenuItem = MenuItemDI,
	children,
	useChannelsDialog = useChannelsDialogDI,
}: CustomerChannelsItemProps) => {
	const [{ isChannelsDialogOpen }, { setIsChannelsDialogOpen }] = useChannelsDialog();

	const Trigger = useCallback(
		(triggerProps: Partial<TriggerProps>) => (
			<MenuItem
				{...triggerProps}
				analytics={{ itemId: 'customer-channels' }}
				iconBefore={<CustomerChannelsIcon spacing="spacious" label="" />}
				onClick={() => {
					setIsChannelsDialogOpen(!isChannelsDialogOpen);
				}}
			>
				{children}
			</MenuItem>
		),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[children, isChannelsDialogOpen, setIsChannelsDialogOpen],
	);

	const Content = useCallback(() => <CustomerChannelsDialog />, []);

	if (__SERVER__) return <Trigger aria-expanded={false} aria-haspopup />;

	return (
		<AkPopup
			popupComponent={PopupComponent}
			trigger={Trigger}
			placement="right"
			content={Content}
			isOpen={isChannelsDialogOpen}
			offset={POPUP_OFFSET}
			onClose={() => setIsChannelsDialogOpen(false)}
		/>
	);
};
const ChannelDialogWrapperStyles = xcss({
	backgroundColor: 'elevation.surface',
	borderRadius: 'border.radius.200',
	color: 'color.text',
	padding: 'space.200',
	zIndex: 'dialog',
	boxShadow: 'elevation.shadow.overlay',
	':focus': {
		outline: 'none',
	},
});
